import {
    getApiErrorMessage,
    IApiAxiosError,
    ILibraryListScope,
    updateLibraryScope,
} from "@/api";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface RenameScopeDialogProps {
    open: boolean;
    onClose: () => void;
    scope: ILibraryListScope;
    onRenamed: () => void;
}

export const RenameScopeDialog = ({
    open,
    onClose,
    scope,
    onRenamed,
}: RenameScopeDialogProps) => {
    const [name, setName] = useState(scope.document_title);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setName(scope.document_title);
    }, [scope.document_title]);

    const { mutate, isPending } = useMutation({
        mutationFn: () =>
            updateLibraryScope(scope.id, { document_title: name.trim() }),
        onSuccess: () => {
            toast.success("Scope renamed successfully");
            onRenamed();
        },
        onError: (err: IApiAxiosError) => {
            setError(getApiErrorMessage(err, "Failed to rename scope"));
        },
    });

    const handleSubmit = () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setError("Name cannot be empty");
            return;
        }
        if (trimmed === scope.document_title) {
            onClose();
            return;
        }
        setError(null);
        mutate();
    };

    const handleClose = () => {
        setName(scope.document_title);
        setError(null);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Rename Scope</DialogTitle>
                </DialogHeader>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                    className="space-y-3"
                >
                    <div className="space-y-1.5">
                        <Label htmlFor="rename-scope-name">Name</Label>
                        <Input
                            id="rename-scope-name"
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onFocus={(e) => e.currentTarget.select()}
                        />
                    </div>
                    {error && (
                        <p className="text-destructive text-sm">{error}</p>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            type="button"
                            onClick={handleClose}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isPending || !name}>
                            {isPending ? (
                                <>
                                    <Loader2 className="mr-2 size-4 animate-spin" />
                                    Saving…
                                </>
                            ) : (
                                "Save"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
